import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { useAdmin } from "./AdminLayout";

const inputCls = "w-full h-11 px-4 bg-[#0F1115] border border-[#272B33] rounded-xl text-sm text-white focus:outline-none focus:border-blue-500";

export default function SettingsPage() {
  const { admin } = useAdmin();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [userForm, setUserForm] = useState(null);
  const [tab, setTab] = useState("genel");

  const load = () => {
    api.get("/admin/settings").then(({ data }) => setSettings(data));
    if (admin?.role === "SUPER_ADMIN") {
      api.get("/admin/auth/users").then(({ data }) => setUsers(data));
      api.get("/admin/audit-logs?limit=30").then(({ data }) => setLogs(data.items));
    }
  };
  useEffect(load, [admin]);

  if (!settings) return null;
  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...settings, shipping_fee: parseFloat(settings.shipping_fee) || 0, free_shipping_threshold: parseFloat(settings.free_shipping_threshold) || 0 };
      const { data } = await api.put("/admin/settings", payload);
      setSettings(data);
      toast.success("Ayarlar kaydedildi");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/auth/users", userForm);
      toast.success("Kullanıcı oluşturuldu"); setUserForm(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const purgeDemo = async () => {
    if (!window.confirm("Tüm DEMO verileri (ürün, kategori, marka, banner, kampanya, kupon) kalıcı olarak silinecek. Emin misiniz?")) return;
    try {
      const { data } = await api.delete("/admin/demo-data");
      toast.success(`Demo veriler silindi: ${JSON.stringify(data.deleted)}`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const tabs = [["genel", "Genel Ayarlar"], ...(admin?.role === "SUPER_ADMIN" ? [["kullanicilar", "Admin Kullanıcıları"], ["loglar", "İşlem Kayıtları"]] : [])];

  return (
    <div className="max-w-4xl space-y-5" data-testid="admin-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">Site Ayarları</h1>
        <div className="flex gap-2 bg-[#181B20] border border-[#272B33] rounded-xl p-1">
          {tabs.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === id ? "bg-blue-600 text-white" : "text-neutral-400"}`}
              data-testid={`settings-tab-${id}`}>{label}</button>
          ))}
        </div>
      </div>

      {tab === "genel" && (
        <form onSubmit={saveSettings} className="space-y-6">
          <section className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
            <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Site Adı</label>
              <input value={settings.site_name || ""} onChange={(e) => set("site_name", e.target.value)} className={inputCls} data-testid="settings-site-name" /></div>
            <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Slogan</label>
              <input value={settings.slogan || ""} onChange={(e) => set("slogan", e.target.value)} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Duyuru Çubuğu</label>
              <input value={settings.announcement_bar || ""} onChange={(e) => set("announcement_bar", e.target.value)} className={inputCls} data-testid="settings-announcement" /></div>
            <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Sabit Kargo Ücreti (₺)</label>
              <input type="number" step="0.01" value={settings.shipping_fee} onChange={(e) => set("shipping_fee", e.target.value)} className={inputCls} data-testid="settings-shipping-fee" /></div>
            <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Ücretsiz Kargo Eşiği (₺)</label>
              <input type="number" step="0.01" value={settings.free_shipping_threshold} onChange={(e) => set("free_shipping_threshold", e.target.value)} className={inputCls} data-testid="settings-free-threshold" /></div>
            <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">İletişim E-posta</label>
              <input value={settings.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} /></div>
            <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Telefon</label>
              <input value={settings.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className={inputCls} /></div>
            <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">WhatsApp Hattı</label>
              <input value={settings.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} /></div>
            <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Instagram</label>
              <input value={settings.instagram || ""} onChange={(e) => set("instagram", e.target.value)} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">SEO Başlığı</label>
              <input value={settings.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">SEO Açıklaması</label>
              <textarea rows={2} value={settings.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={`${inputCls} py-3 resize-none`} /></div>
          </section>

          <section className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6">
            <h2 className="font-semibold text-white text-sm mb-1">Havale / EFT Banka Bilgileri</h2>
            <p className="text-xs text-neutral-500 mb-4">Checkout'ta Havale/EFT seçeneğini seçen müşteriye gösterilir.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Banka Adı</label>
                <input value={settings.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} className={inputCls} data-testid="settings-bank-name" /></div>
              <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Hesap Sahibi</label>
                <input value={settings.bank_account_holder || ""} onChange={(e) => set("bank_account_holder", e.target.value)} className={inputCls} data-testid="settings-bank-holder" /></div>
              <div className="sm:col-span-2"><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">IBAN</label>
                <input value={settings.bank_iban || ""} onChange={(e) => set("bank_iban", e.target.value)} className={inputCls} data-testid="settings-bank-iban" /></div>
              <div className="sm:col-span-2"><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Havale Notu</label>
                <textarea rows={2} value={settings.havale_note || ""} onChange={(e) => set("havale_note", e.target.value)} className={`${inputCls} py-3 resize-none`} data-testid="settings-havale-note" /></div>
              <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Ödeme Süresi (dakika)</label>
                <input type="number" min="5" max="1440" value={settings.payment_timeout_minutes ?? 30} onChange={(e) => set("payment_timeout_minutes", parseInt(e.target.value, 10) || 30)} className={inputCls} data-testid="settings-payment-timeout" />
                <p className="text-[10px] text-neutral-600 mt-1">Kart siparişi bu sürede ödenmezse "Ödeme Süresi Doldu" olur ve stok rezervi serbest kalır.</p></div>
            </div>
          </section>
          <div className="flex items-center gap-3">
            <button type="submit" className="h-11 px-8 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500" data-testid="settings-save">Kaydet</button>
          </div>

          <section className="bg-rose-500/5 border border-rose-500/20 rounded-2xl p-6">
            <h2 className="font-semibold text-rose-400 text-sm mb-2">Tehlikeli Bölge</h2>
            <p className="text-xs text-neutral-500 mb-4">Demo ürün, kategori, marka, banner, kampanya ve kuponları kalıcı olarak siler. Gerçek verilere dokunmaz.</p>
            <button type="button" onClick={purgeDemo} className="h-10 px-5 rounded-xl bg-rose-600/15 text-rose-400 text-sm font-semibold hover:bg-rose-600/25 flex items-center gap-2" data-testid="purge-demo-btn">
              <Trash2 size={15} /> Demo Verileri Temizle
            </button>
          </section>
        </form>
      )}

      {tab === "kullanicilar" && admin?.role === "SUPER_ADMIN" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setUserForm({ name: "", email: "", password: "", role: "EDITOR" })}
              className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center gap-2" data-testid="admin-user-new">
              <Plus size={15} /> Yeni Admin
            </button>
          </div>
          {userForm && (
            <form onSubmit={saveUser} className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5 grid sm:grid-cols-5 gap-3 items-end" data-testid="admin-user-form">
              <input required placeholder="Ad Soyad" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} className={inputCls} />
              <input required type="email" placeholder="E-posta" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} className={inputCls} />
              <input required type="password" minLength={8} placeholder="Şifre (min 8)" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} className={inputCls} />
              <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} className={inputCls}>
                <option value="EDITOR">EDITOR</option><option value="ADMIN">ADMIN</option><option value="SUPER_ADMIN">SUPER_ADMIN</option>
              </select>
              <button type="submit" className="h-11 rounded-xl bg-blue-600 text-white text-sm font-semibold" data-testid="admin-user-save">Oluştur</button>
            </form>
          )}
          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden">
            {users.map((u) => (
              <div key={u.admin_id} className="flex items-center justify-between px-5 py-3.5 border-b border-[#272B33]/50 last:border-0" data-testid={`admin-user-row-${u.email}`}>
                <div>
                  <p className="text-sm text-white font-medium">{u.name}</p>
                  <p className="text-[11px] text-neutral-500">{u.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono-ed px-2.5 py-1 rounded-full bg-blue-600/15 text-blue-400">{u.role}</span>
                  <button onClick={async () => { await api.put(`/admin/auth/users/${u.admin_id}`, { active: !u.active }); load(); }}
                    className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${u.active ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}
                    data-testid="admin-user-toggle">{u.active ? "Aktif" : "Pasif"}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "loglar" && admin?.role === "SUPER_ADMIN" && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden" data-testid="audit-log-list">
          {logs.map((l) => (
            <div key={l.log_id} className="px-5 py-3 border-b border-[#272B33]/50 last:border-0 text-xs">
              <div className="flex justify-between">
                <span className="text-white font-medium">{l.action} / {l.entity}</span>
                <span className="text-neutral-500">{fmtDate(l.created_at)}</span>
              </div>
              <p className="text-neutral-500 mt-0.5">{l.admin_email} · <span className="font-mono-ed">{JSON.stringify(l.details).slice(0, 140)}</span></p>
            </div>
          ))}
          {logs.length === 0 && <p className="text-center text-neutral-500 text-sm py-10">Kayıt yok</p>}
        </div>
      )}
    </div>
  );
}
